import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  doc, 
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { 
  Target, Compass, Zap, Lightbulb, BookOpen,
  MessageCircle, Loader2, Info, BarChart3
} from 'lucide-react';
import './NotificationHistory.css';

// Mapper la phase de l'objectif à une configuration visuelle
const getPhaseConfig = (phase, contentFormat = null) => {
  const phases = {
    'ambition': {
      label: 'Ambition',
      icon: Target,
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
      borderColor: 'border-purple-200'
    },
    'stratégie': {
      label: 'Stratégie',
      icon: Compass,
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-200'
    },
    'action': {
      label: 'Plan d\'action',
      icon: Zap,
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
      borderColor: 'border-green-200'
    },
    'inspiration': {
      label: 'Inspiration',
      icon: Lightbulb,
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-200'
    },
    'exploration': {
      label: 'Exploration',
      icon: BookOpen,
      bgColor: 'bg-teal-50',
      textColor: 'text-teal-700',
      borderColor: 'border-teal-200'
    },
    'progression': {
      label: contentFormat === 'simple' ? 'Faisons le point ensemble' : 'Votre progression en un coup d\'œil',
      icon: contentFormat === 'simple' ? MessageCircle : BarChart3,
      bgColor: contentFormat === 'simple' ? 'bg-cyan-50' : 'bg-indigo-50',
      textColor: contentFormat === 'simple' ? 'text-cyan-700' : 'text-indigo-700',
      borderColor: contentFormat === 'simple' ? 'border-cyan-200' : 'border-indigo-200'
    }
  };
  
  return phases[phase] || phases['ambition'];
};

// Obtenir le titre court selon le type de notification
const getNotificationShortTitle = (notification) => {
  const type = notification.notificationType;
  const contentFormat = notification.contentFormat;
  
  switch (type) {
    case 'TYPE_1_STRATEGY':
    case 'strategy_pre_generated':
      return "Découvrez votre stratégie personnalisée";
    case 'TYPE_2_ACTION_PLAN':
    case 'action_plan_pre_generated':
      return "Votre plan d'action est prêt !";
    case 'TYPE_3_INSPIRATION':
    case 'inspiration_search_suggested':
      return "Consulter vos sources d'inspiration";
    case 'TYPE_4_KNOWLEDGE_BASE':
      return "Explorez votre base de connaissances";
    case 'TYPE_5_PROGRESS_REPORT':
      if (contentFormat === 'simple') {
        return "Aladinn peut vous guider pour réaliser votre projet";
      }
      return "Consultez vos notes : décisions, défis, recommandations";
    default:
      switch (notification.phase) {
        case 'ambition':
          return "Structurons votre ambition";
        case 'stratégie':
          return "Stratégie prête à découvrir";
        case 'action':
          return "Plan d'action personnalisé";
        case 'inspiration':
          return "Ressources d'inspiration";
        case 'exploration':
          return "Organisez vos connaissances";
        default:
          return "Nouvelle notification";
      }
  }
};

// Obtenir le message preview selon le type
const getNotificationMessage = (notification) => {
  const type = notification.notificationType;
  const contentFormat = notification.contentFormat;
  
  switch (type) {
    case 'TYPE_1_STRATEGY':
    case 'strategy_pre_generated':
      return "J'ai analysé votre objectif et préparé une stratégie sur mesure pour vous guider.";
    case 'TYPE_2_ACTION_PLAN':
    case 'action_plan_pre_generated':
      return "J'ai transformé votre stratégie en un plan d'action concret et mesurable.";
    case 'TYPE_3_INSPIRATION':
    case 'inspiration_search_suggested':
      return "Découvrez les ressources pour accélérer votre progression.";
    case 'TYPE_4_KNOWLEDGE_BASE':
      return "Votre base de connaissances est prête à être exploitée avec l'IA contextuelle.";
    case 'TYPE_5_PROGRESS_REPORT':
      if (contentFormat === 'simple') {
        return "Prenons quelques minutes pour compléter vos notes d'avancement avec l'aide d'Aladinn.";
      }
      return "Vos notes d'avancement sont disponibles dans le workspace.";
    default:
      return "Aladinn a une suggestion personnalisée pour vous aider à progresser.";
  }
};

const NotificationHistory = ({ userId }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [objectiveTitles, setObjectiveTitles] = useState({});
  const [objectiveMetrics, setObjectiveMetrics] = useState({});
  const [objectivesData, setObjectivesData] = useState({}); // ✅ NOUVEAU: Stocker les données complètes des objectifs
  
  // Fonction pour calculer les métriques d'une étape
  const calculateStepMetrics = useCallback((stepNotes) => {
    const metrics = {
      decisionsCount: 0,
      questionsCount: 0,
      recommendationsCount: 0
    };

    if (!stepNotes || typeof stepNotes !== 'object') {
      return metrics;
    }

    if (stepNotes.decisions && stepNotes.decisions.trim().length > 0) {
      const sections = stepNotes.decisions.split('\n\n').filter(s => s.trim());
      metrics.decisionsCount = sections.length;
    }

    if (stepNotes.questions && stepNotes.questions.trim().length > 0) {
      const sections = stepNotes.questions.split('\n\n').filter(s => s.trim());
      metrics.questionsCount = sections.length;
    }

    if (stepNotes.ai_recommendations && stepNotes.ai_recommendations.trim().length > 0) {
      const sections = stepNotes.ai_recommendations.split('\n\n').filter(s => s.trim());
      metrics.recommendationsCount = sections.length;
    }

    return metrics;
  }, []);

  // Charger les métriques d'un objectif depuis Firebase
  const loadObjectiveMetrics = useCallback(async (objectifId) => {
    if (objectiveMetrics[objectifId]) {
      return objectiveMetrics[objectifId];
    }

    try {
      if (!userId || !objectifId) return null;

      const objectifRef = doc(db, 'users', userId, 'competences', objectifId);
      const objectifSnap = await getDoc(objectifRef);

      if (objectifSnap.exists()) {
        const objectiveData = objectifSnap.data();
        
        const totalMetrics = {
          decisions: 0,
          challenges: 0,
          recommendations: 0
        };

        if (objectiveData.stepNotes && typeof objectiveData.stepNotes === 'object') {
          Object.values(objectiveData.stepNotes).forEach(stepData => {
            const stepMetrics = calculateStepMetrics(stepData);
            totalMetrics.decisions += stepMetrics.decisionsCount;
            totalMetrics.challenges += stepMetrics.questionsCount;
            totalMetrics.recommendations += stepMetrics.recommendationsCount;
          });
        }

        setObjectiveMetrics(prev => ({
          ...prev,
          [objectifId]: totalMetrics
        }));

        return totalMetrics;
      }
    } catch (error) {
      console.error('Erreur chargement métriques objectif:', error);
    }
    return null;
  }, [userId, objectiveMetrics, calculateStepMetrics]);

  // ✅ NOUVEAU: Fonction pour charger les données complètes d'un objectif
  const loadObjectiveData = useCallback(async (objectifId) => {
    if (objectivesData[objectifId]) {
      return objectivesData[objectifId];
    }

    try {
      if (!userId || !objectifId) return null;

      const objectifRef = doc(db, 'users', userId, 'competences', objectifId);
      const objectifSnap = await getDoc(objectifRef);

      if (objectifSnap.exists()) {
        const data = { id: objectifSnap.id, ...objectifSnap.data() };
        
        setObjectivesData(prev => ({
          ...prev,
          [objectifId]: data
        }));

        return data;
      }
    } catch (error) {
      console.error('Erreur chargement données objectif:', error);
    }
    return null;
  }, [userId, objectivesData]);

  // Charger les notifications depuis Firebase
  const loadNotifications = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Charger l'historique des notifications
      const notificationsRef = collection(db, 'users', userId, 'notification_history');
      const notificationsQuery = query(
        notificationsRef,
        orderBy('sentAt', 'desc'),
        limit(50)
      );

      const notificationsSnapshot = await getDocs(notificationsQuery);
      const notificationsData = notificationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        sentAt: doc.data().sentAt?.toDate?.() || new Date()
      }));

      setNotifications(notificationsData);

      // Charger les titres et données des objectifs
      const objectifIds = [...new Set(notificationsData.map(n => n.objectifId).filter(Boolean))];
      const titles = {};
      
      for (const objectifId of objectifIds) {
        try {
          const objectifRef = doc(db, 'users', userId, 'competences', objectifId);
          const objectifSnap = await getDoc(objectifRef);
          
          if (objectifSnap.exists()) {
            const data = objectifSnap.data();
            titles[objectifId] = data.shortTitle || data.title || 'Sans titre';
            
            // ✅ Charger aussi les données complètes de l'objectif
            await loadObjectiveData(objectifId);
            
            // Charger les métriques pour les notifications TYPE_5
            if (notificationsData.some(n => 
              n.objectifId === objectifId && 
              n.notificationType === 'TYPE_5_PROGRESS_REPORT'
            )) {
              await loadObjectiveMetrics(objectifId);
            }
          }
        } catch (error) {
          console.error(`Erreur chargement objectif ${objectifId}:`, error);
          titles[objectifId] = 'Objectif introuvable';
        }
      }

      setObjectiveTitles(titles);

    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, loadObjectiveMetrics, loadObjectiveData]);

  // Marquer une notification comme lue
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const notificationRef = doc(db, 'users', userId, 'notification_history', notificationId);
      await updateDoc(notificationRef, {
        opened: true,
        openedAt: serverTimestamp()
      });

      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, opened: true, openedAt: new Date() }
            : n
        )
      );
    } catch (error) {
      console.error('Erreur marquage lecture:', error);
    }
  }, [userId]);

  // ✅ CORRIGÉ: Gérer le clic sur une notification avec navigation améliorée
  const handleNotificationClick = async (notification) => {
    // Marquer comme lue
    if (!notification.opened) {
      await markAsRead(notification.id);
    }

    // Si déjà répondu, ne pas permettre de cliquer
    if (notification.responded) {
      return;
    }

    const objectifId = notification.objectifId;
    
    // ✅ Charger les données complètes de l'objectif si pas déjà en cache
    const objectiveData = await loadObjectiveData(objectifId);
    
    if (!objectiveData) {
      console.error('Impossible de charger les données de l\'objectif');
      return;
    }

    // ✅ Déterminer l'étape cible selon le type de notification
    let targetStep = 'strategy';
    
    switch (notification.notificationType) {
      case 'TYPE_1_STRATEGY':
      case 'strategy_pre_generated':
        targetStep = 'strategy';
        break;
        
      case 'TYPE_2_ACTION_PLAN':
      case 'action_plan_pre_generated':
        targetStep = 'action';
        break;
        
      case 'TYPE_3_INSPIRATION':
      case 'inspiration_search_suggested':
        targetStep = 'inspiration';
        break;
        
      case 'TYPE_4_KNOWLEDGE_BASE':
        targetStep = 'exploration';
        break;
        
      case 'TYPE_5_PROGRESS_REPORT':
        // Pour les rapports de progression, aller au chat
        navigate(`/chat/${objectifId}`, {
          state: {
            competence: objectiveData,
            fromNotification: true
          }
        });
        return;
        
      default:
        // Par défaut, utiliser la phase de la notification
        const phaseToStepMap = {
          'ambition': 'ambition',
          'stratégie': 'strategy',
          'action': 'action',
          'inspiration': 'inspiration',
          'exploration': 'exploration'
        };
        targetStep = phaseToStepMap[notification.phase] || 'strategy';
    }

    // ✅ Navigation avec state complet, comme dans ObjectifsList
    navigate('/HomePage', {
      state: {
        competence: objectiveData,
        targetStep: targetStep,
        fromNotificationHistory: true
      }
    });
  };

  // Charger les notifications au montage
  useEffect(() => {
    loadNotifications();
  }, [userId]); // ✅ Ne dépend que de userId


  // Formater la date
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Afficher les métriques de progression pour TYPE_5
  const renderProgressMetrics = (notification) => {
    if (notification.notificationType !== 'TYPE_5_PROGRESS_REPORT') {
      return null;
    }

    if (notification.contentFormat === 'simple') {
      return null;
    }

    const metrics = objectiveMetrics[notification.objectifId];
    if (!metrics) return null;

    const { decisions, challenges, recommendations } = metrics;
    const totalNotes = decisions + challenges + recommendations;

    if (totalNotes === 0) return null;

    return (
      <div className="progress-metrics-compact">
        <div className="metrics-row">
          {decisions > 0 && (
            <span className="metric-pill metric-decisions">
              ✅ {decisions} décision{decisions > 1 ? 's' : ''}
            </span>
          )}
          {challenges > 0 && (
            <span className="metric-pill metric-challenges">
              ❓ {challenges} défi{challenges > 1 ? 's' : ''}
            </span>
          )}
          {recommendations > 0 && (
            <span className="metric-pill metric-recommendations">
              💡 {recommendations} recommandation{recommendations > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="notification-history">
        <div className="loading-state">
          <Loader2 className="spinner-icon" size={40} />
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-history">
      {/* Liste des notifications */}
      <div className="notifications-list">
        {notifications.length === 0 ? (
          <div className="empty-state-box">
            <MessageCircle size={48} className="empty-icon" />
            <h3>Aucune notification</h3>
            <p>Vous recevrez des suggestions personnalisées pour faire avancer vos objectifs</p>
          </div>
        ) : (
          notifications.map((notification) => {
            let displayPhase = notification.phase || 'ambition';
            if (notification.notificationType === 'TYPE_5_PROGRESS_REPORT') {
              displayPhase = 'progression';
            }
            
            const phaseConfig = getPhaseConfig(displayPhase, notification.contentFormat);
            const PhaseIcon = phaseConfig.icon;
            
            return (
              <div key={notification.id} className="notification-card-wrapper">
                <div 
                  onClick={() => handleNotificationClick(notification)}
                  className={`notification-card ${!notification.responded ? 'clickable' : 'completed'} ${!notification.opened ? 'unread' : ''}`}
                >
                  {/* Badge phase */}
                  <div className={`phase-badge ${phaseConfig.bgColor} ${phaseConfig.borderColor}`}>
                    <PhaseIcon size={16} className={phaseConfig.textColor} />
                    <span className={phaseConfig.textColor}>{phaseConfig.label}</span>
                  </div>

                  {/* Contenu */}
                  <div className="notification-content">
                    <div className="notification-header">
                      <h3 className="notification-title">
                        {getNotificationShortTitle(notification)}
                      </h3>
                      <span className="notification-date">{formatDate(notification.sentAt)}</span>
                    </div>

                    <div className="notification-objective">
                      <span className="objective-label">Objectif :</span>
                      <span className="objective-title">
                        {objectiveTitles[notification.objectifId] || 'Chargement...'}
                      </span>
                    </div>

                    <p className="notification-preview">
                      {getNotificationMessage(notification)}
                    </p>

                    {/* Métriques pour TYPE_5 */}
                    {renderProgressMetrics(notification)}
                  </div>

                  {/* Indicateur non lu */}
                  {!notification.opened && (
                    <div className="unread-indicator"></div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Bloc d'explication */}
      <div className="info-explanation-box">
        <div className="info-header">
          <Info size={22} className="info-icon" />
          <h3>Comment fonctionnent les notifications ?</h3>
        </div>
        <div className="info-content">
          <div className="info-step">
            <div className="step-number">1</div>
            <div className="step-text">
              <strong>Aladinn vous envoie un email</strong>
              <p>Avec des questions adaptées à votre objectif et votre progression</p>
            </div>
          </div>
          <div className="info-step">
            <div className="step-number">2</div>
            <div className="step-text">
              <strong>Vous répondez en quelques minutes</strong>
              <p>Dans le chat guidé, à votre rythme</p>
            </div>
          </div>
          <div className="info-step">
            <div className="step-number">3</div>
            <div className="step-text">
              <strong>Vous recevez des recommandations</strong>
              <p>Personnalisées selon votre phase (ambition, stratégie, action, etc.)</p>
            </div>
          </div>
          <div className="info-step">
            <div className="step-number">4</div>
            <div className="step-text">
              <strong>Vos réponses enrichissent vos notes</strong>
              <p>Votre progression est documentée automatiquement</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationHistory;
